import React, { ComponentProps, useState } from 'react';

import { useIntl } from 'react-intl';

import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { Overlay } from '@components/organisms/Overlay';
import { CardHeader } from '@components/molecules/CardHeader/CardHeader';

import { useWishlist } from '@sdk/hook/useWishlist';

import { compareAttributes } from '@sdk/providers/utils';
import contactFormIcon from 'images/contact-form-icon.svg';
import saveIcon from 'images/save-icon.svg';
import saveInitial from 'images/save-initial.svg';
import saveSuccessIcon from 'images/save-success-icon.svg';
import shareIcon from 'images/share-icon.svg';

import { SaveProjectForm } from './SaveProjectForm';
import { SaveSelect } from './SaveSelect';
import { ShareIcons } from './ShareIcons';

import * as S from './styles';
import { ShareState, IProps } from './types';

const transformProductName = (name: string): string => {
  const n = name.split('-').join(' ');
  return `${n[0].toUpperCase()}${n.slice(1)}`;
};

function ShareProjectSelect(props: ComponentProps<typeof SaveSelect>) {
  return <SaveSelect {...props} />;
}

function ShareProjectForm(props: ComponentProps<typeof SaveProjectForm>) {
  return <SaveProjectForm {...props} />;
}

function ShareProjectSuccessIcons(props: ComponentProps<typeof ShareIcons>) {
  const intl = useIntl();
  return (
    <>
      <p>
        {intl.formatMessage({
          defaultMessage:
            'Share your design with your friends and get their opinion:',
          id: '9hDjZq',
        })}
      </p>
      <ShareIcons {...props} />
    </>
  );
}

function ShareProjectIcons(props: ComponentProps<typeof ShareIcons>) {
  return <ShareIcons {...props} />;
}

function ShareProjectAddedToWishlist() {
  const intl = useIntl();
  return (
    <S.ContentWishlist>
      <span>
        {intl.formatMessage({
          defaultMessage: 'We will keep you design safe.',
          id: 'WDiiAE',
        })}
      </span>
    </S.ContentWishlist>
  );
}

function ShareProjectRemovedToWishlist() {
  const intl = useIntl();
  return (
    <S.ContentWishlist>
      <span>
        {intl.formatMessage({
          defaultMessage: 'You creation is lost foreever.',
          id: 'kS0mq9',
        })}
      </span>
    </S.ContentWishlist>
  );
}

const ShareProject: React.FC<IProps> = ({
  initialState,
  productCategory,
  variantId,
  productId,
  productImage,
  productName,
  productUrl,
  selectedProductAttributes,
}) => {
  const intl = useIntl();
  const modal = useModal();

  const [state, setState] = useState<ShareState>(
    initialState || ShareState.initial
  );

  const selectOption = (newState: ShareState) => {
    setState(newState);
  };

  const isShowLeft = [
    ShareState.initial,
    ShareState.save,
    ShareState.saveSuccess,
    ShareState.share,
    ShareState.addToWishlist,
    ShareState.removeToWishlist,
  ].includes(state);

  const { items } = useWishlist();

  const addedToWishlist =
    items?.some(({ variant, productAttributes }) => {
      if (variant?.id !== variantId) {
        return false;
      }
      return compareAttributes(productAttributes, selectedProductAttributes);
    }) || false;

  const FIXTURES = {
    [ShareState.initial]: {
      content: ShareProjectSelect,
      image: saveInitial,
      text: '',
      title: intl.formatMessage({
        defaultMessage: 'Save for later your doors:',
        id: 'IeC4nn',
      }),
    },
    [ShareState.save]: {
      content: ShareProjectForm,
      image: saveIcon,
      text: intl.formatMessage({
        defaultMessage: 'It deserves to be saved and sent to your inbox.',
        id: '4HGt+A',
      }),
      title: intl.formatMessage({
        defaultMessage: 'Your design is aDOORable!',
        id: 'Q6TDFU',
      }),
    },
    [ShareState.saveSuccess]: {
      content: ShareProjectSuccessIcons,
      image: saveSuccessIcon,
      text: intl.formatMessage({
        defaultMessage:
          'You can easily watch it later. You can open it when you want on any device you would be using.',
        id: 'f0J+wF',
      }),
      title: intl.formatMessage({
        defaultMessage: 'The link to your project was sent to your email',
        id: 'Ai+JIH',
      }),
    },
    [ShareState.share]: {
      content: ShareProjectIcons,
      image: contactFormIcon,
      text: intl.formatMessage({
        defaultMessage:
          'Share designs with your friends. Let’s see what they think!',
        id: 'VcLJvj',
      }),
      title: intl.formatMessage({
        defaultMessage:
          'What do your family and relatives really think? Just ask.',
        id: 'GdKD/W',
      }),
    },
    [ShareState.addToWishlist]: {
      content: ShareProjectAddedToWishlist,
      image: shareIcon,
      text: '',
      title: intl.formatMessage({
        defaultMessage: 'Save for later',
        id: 'U/jWmk',
      }),
    },
    [ShareState.removeToWishlist]: {
      content: ShareProjectRemovedToWishlist,
      image: shareIcon,
      text: '',
      title: intl.formatMessage({
        defaultMessage: 'Save for later',
        id: 'U/jWmk',
      }),
    },
  };

  return (
    <Overlay position="fit" show={modal.visible} hide={modal.remove}>
      <S.Modal>
        <S.Wrapper>
          {isShowLeft && (
            <S.Left>
              {productImage && <S.ProductImage src={productImage} alt="icon" />}
              {productCategory && (
                <S.ProductCategory>{productCategory}</S.ProductCategory>
              )}

              <S.ProductTitle>
                {transformProductName(productName)}
              </S.ProductTitle>
            </S.Left>
          )}
          <S.Right fullWidth={!isShowLeft}>
            <S.Header>
              <CardHeader
                divider
                formImage={FIXTURES[state].image}
                onHide={modal.remove}
              >
                {FIXTURES[state].title}
              </CardHeader>
            </S.Header>
            <S.Content>
              {FIXTURES[state].text && <S.Text>{FIXTURES[state].text}</S.Text>}
              {FIXTURES[state].content({
                addedToWishlist,
                onSuccess: () => {
                  if (state === ShareState.save) {
                    setState(ShareState.saveSuccess);
                  }
                },
                productId,
                productImage,
                selectOption,
                selectedProductAttributes,
                productUrl,
                variantId,
              })}
            </S.Content>
          </S.Right>
        </S.Wrapper>
      </S.Modal>
    </Overlay>
  );
};

export default NiceModal.create(ShareProject);
